/**
 * 작성자 : 김광민
 * 날짜 : 2024.02.15
 * 기능 : 발생된 이슈와 관련된 일러스트 및 메시지를 함께 출력하는 컴포넌트
 */
/**
 * 수정자 : 박승호
 * 날짜 : 2024.10.14
 * 수정 이유 : 타입 추가하여 이미지를 일일이 prop에 넣어주는 번거롱움 제거
 */

import styled from 'styled-components';

import defaultIllustrator from '../assets/images/illustration/305.svg';
import locationIllustrator from '../assets/images/illustration/23.svg';
import selectIllustrator from '../assets/images/illustration/31.svg';
import imgIllustrator from '../assets/images/illustration/61.svg';
import chartIllustrator from '../assets/images/illustration/74.svg';
import textIllustrator from '../assets/images/illustration/78.svg';
import warningIllustrator from '../assets/images/illustration/128.svg';
import completeIllustrator from '../assets/images/illustration/283.svg';
import { useTranslation } from 'react-i18next';

interface StyleProps {
  size: number;
}

const Root = styled.div<StyleProps>`
  display: flex;
  flex-grow: 1 !important;
  flex-direction: column;
  align-items: center !important;
  justify-content: center;
  gap: 1rem;
  user-select: none;
  background-color: transparent;
  // background-color: var(--tonal-light);
  border-radius: 0.5rem;
  width: 100%;
  height: 100%;

  img {
    width: ${(props: StyleProps) => props.size}rem;
    object-fit: cover;
    vertical-align: bottom;
    -webkit-user-drag: none;
    @media (min-width: 768px) {
      width: ${(props: StyleProps) => props.size + 2}rem;
    }
    @media (min-width: 1025px) {
      width: ${(props: StyleProps) => props.size + 4}rem;
    }
  }

  p {
    padding: 0.5rem 0.75rem;
    color: var(--filled-violet);
    background-color: var(--tonal-violet);
    border-radius: 0.25rem;
    font-size: 0.875rem;
    letter-spacing: 0;
    font-weight: 400;
    word-break: keep-all;
    text-align: center;
  }
`;

type Props = {
  text?: string; // 내용
  type?: 'data' | 'select' | 'location' | 'text' | 'chart' | 'img' | 'mobile' | 'error' | 'complete'; // 이미지 타입
  size?: number; // 이미지 크기 ( rem 단위 )
  isImg?: boolean; // 이미지 여부
  icon?: string; // 아이콘
};

const IssueGuide = ({ text = '데이터가 존재하지 않습니다', type = 'data', size = 12, isImg = true, icon = '' }: Props) => {
  const { t } = useTranslation();

  const imgRender = () => {
    switch (type) {
      case 'data': // 빈 데이터
        return <img src={defaultIllustrator} alt={text} />;
      case 'select': // 선택 지시
        return <img src={selectIllustrator} alt={text} />;
      case 'location': // 위치
        return <img src={locationIllustrator} alt={text} />;
      case 'text': // 빈 텍스트 및 텍스트 관련
        return <img src={textIllustrator} alt={text} />;
      case 'chart': // 빈 차트 및 차트 관련
        return <img src={chartIllustrator} alt={text} />;
      case 'img': // 빈 이미지 및 이미지 관련
        return <img src={imgIllustrator} alt={text} />;
      case 'mobile': // 모바일 미지원
        return <img src={warningIllustrator} alt={text} />;
      case 'error': // 실패 및 오류
        return <img src={warningIllustrator} alt={text} />;
      case 'complete': // 성공
        return <img src={completeIllustrator} alt={text} />;
      default: // 기본 - 빈 데이터
        return <img src={defaultIllustrator} alt={text} />;
    }
  };

  return (
    <Root size={size} className='issueBox'>
      {isImg ? imgRender() : null}
      <p>
        {icon && <span className='material-symbols-rounded'>{icon}</span>}
        {t(text)
          ?.split('<br/>')
          ?.map((line, index) => (
            <span key={index}>
              {line}
              {index < (t(text)?.split('<br/>')?.length || 0) - 1 && <br />}
            </span>
          ))}
      </p>
    </Root>
  );
};

export default IssueGuide;
